/**
 * Builds an optimized Contentful Images API URL.
 *
 * Contentful serves the RAW asset (frequently a large PNG/JPG) unless transform
 * query params are supplied. Appending `fm`/`w`/`q` makes Contentful's own image
 * CDN convert + resize at its edge.
 *
 * This is deliberately NOT routed through `next/image`: the asset is already on a
 * CDN (double-optimizing re-encodes the same image twice), and Contentful's Images
 * API already handles format negotiation and resizing at the edge.
 *
 * Contentful asset URLs come back protocol-relative (e.g. `//images.ctfassets.net/...`).
 *
 * @param {string} url   Raw Contentful asset URL (protocol-relative or absolute).
 * @param {object} [opts]
 * @param {number} [opts.width]   Target width in px (Contentful `w`).
 * @param {number} [opts.quality] Compression quality 1-100 (Contentful `q`, default 70).
 * @param {string} [opts.format]  Output format (Contentful `fm`, default 'webp').
 * @returns {string|null} Absolute https URL with transform params, or null if no url.
 */
export const getContentfulImageUrl = (
  url,
  { width, quality = 70, format = 'webp' } = {}
) => {
  if (!url) return null;

  // Normalize to an absolute https URL.
  const absoluteUrl = url.startsWith('http') ? url : `https:${url}`;

  // Only Contentful's Images API understands these params; leave other hosts alone.
  if (!absoluteUrl.includes('images.ctfassets.net')) return absoluteUrl;

  const params = new URLSearchParams();
  if (format) params.set('fm', format);
  if (width) params.set('w', String(width));
  if (quality) params.set('q', String(quality));

  const separator = absoluteUrl.includes('?') ? '&' : '?';
  return `${absoluteUrl}${separator}${params.toString()}`;
};

/**
 * Generates a srcset string for responsive images.
 *
 * Produces multiple image URLs at different widths for the browser to select from
 * based on viewport size and pixel density (e.g., "url?w=400 400w, url?w=800 800w").
 *
 * @param {string} url   Raw Contentful asset URL (protocol-relative or absolute).
 * @param {object} [opts]
 * @param {number[]} [opts.widths=[400, 800, 1200, 1600]] Array of widths in px.
 * @param {number} [opts.quality=70] Compression quality 1-100.
 * @param {string} [opts.format='webp'] Output format.
 * @returns {string|null} srcset string or null if no url.
 */
export const getContentfulImageSrcSet = (
  url,
  { widths = [400, 800, 1200, 1600], quality = 70, format = 'webp' } = {}
) => {
  if (!url) return null;

  return widths
    .map((width) => {
      const imageUrl = getContentfulImageUrl(url, { width, quality, format });
      return `${imageUrl} ${width}w`;
    })
    .join(', ');
};

/**
 * Returns complete props for responsive <img> tags.
 *
 * Provides src (fallback), srcSet (multiple widths), and sizes (viewport hints)
 * for optimal image loading across devices. Spread into <img> tag:
 * <img {...getResponsiveImageProps(url)} alt="..." />
 *
 * @param {string} url   Raw Contentful asset URL (protocol-relative or absolute).
 * @param {object} [opts]
 * @param {number[]} [opts.widths=[400, 800, 1200, 1600]] Array of widths in px.
 * @param {string} [opts.sizes] Sizes attribute (viewport hints for browser).
 * @param {number} [opts.quality=70] Compression quality 1-100.
 * @param {string} [opts.format='webp'] Output format.
 * @returns {object|null} Object with { src, srcSet, sizes } or null if no url.
 */
export const getResponsiveImageProps = (
  url,
  {
    widths = [400, 800, 1200, 1600],
    sizes = '100vw',
    quality = 70,
    format = 'webp',
  } = {}
) => {
  if (!url) return null;

  return {
    src: getContentfulImageUrl(url, {
      width: widths[widths.length - 1],
      quality,
      format,
    }),
    srcSet: getContentfulImageSrcSet(url, { widths, quality, format }),
    sizes,
  };
};

export default getContentfulImageUrl;
